export type NotificationType =
  | 'task_due_soon'
  | 'task_overdue'
  | 'task_starting'
  | 'task_assigned'
  | 'task_completed'
  | 'workspace_invitation'
  | 'workspace_member_added'
  | 'workspace_member_removed'
  | 'workspace_member_role_changed'
  | 'workspace_badge_earned'
  | 'workspace_goal_reached'
  | 'workspace_kudos_received'
  | 'workspace_weekly_team_summary'
  | 'workspace_llm_access_request'
  | 'space_invitation'
  | 'space_invitation_cancelled'
  | 'space_invitation_declined'
  | 'space_joined'
  | 'space_member_added'
  | 'space_member_removed'
  | 'space_member_role_changed'
  | 'space_shared'
  | 'comment_added'
  | 'mention'
  | 'reminder'
  | 'agent_failure'
  | 'agent_recovered'
  | 'integration_failure'
  | 'integration_recovered'
  | 'performance_alert'
  | 'system_alert'
  | 'sidecar_completion'
  | 'daily_report'
  | 'crm_contact_assigned'

export type NotificationChannel = 'desktop' | 'email' | 'in_app'

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent'

export type NotificationStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed'

export interface NotificationPreference {
  enabled: boolean
  channels: NotificationChannel[]
  priority: NotificationPriority
  advanceTime?: number
}

export interface NotificationSettings {
  id: string
  userId: string
  // Task notifications
  taskDueSoon: NotificationPreference
  taskOverdue: NotificationPreference
  taskStarting: NotificationPreference
  taskAssigned: NotificationPreference
  taskCompleted: NotificationPreference
  // Workspace notifications
  workspaceInvitation: NotificationPreference
  workspaceMemberAdded: NotificationPreference
  workspaceMemberRemoved: NotificationPreference
  workspaceMemberRoleChanged: NotificationPreference
  // Space notifications
  spaceShared: NotificationPreference
  // Comment notifications
  commentAdded: NotificationPreference
  mention: NotificationPreference
  // System notifications
  agentFailure: NotificationPreference
  agentRecovered: NotificationPreference
  integrationFailure: NotificationPreference
  integrationRecovered: NotificationPreference
  performanceAlert: NotificationPreference
  systemAlert: NotificationPreference
  sidecarCompletion: NotificationPreference
  dailyReport: NotificationPreference
  // General settings
  quietHours: {
    enabled: boolean
    startTime: string // HH:MM format
    endTime: string // HH:MM format
    timezone: string
  }
  emailDigest: {
    enabled: boolean
    frequency: 'daily' | 'weekly' | 'never'
    time: string // HH:MM format
  }
  desktopNotifications: {
    enabled: boolean
    showPreview: boolean
    sound: boolean
  }
  createdAt: Date
  updatedAt: Date
}

export interface Notification {
  id: string
  userId: string
  type: NotificationType
  title: string
  message: string
  data?: Record<string, unknown>
  priority: NotificationPriority
  channels: NotificationChannel[]
  status: NotificationStatus
  scheduledFor?: Date
  sentAt?: Date
  readAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface NotificationTemplate {
  type: NotificationType
  title: string
  message: string
  defaultChannels: NotificationChannel[]
  defaultPriority: NotificationPriority
}

export type NotificationSettingsKey =
  | 'taskDueSoon'
  | 'taskOverdue'
  | 'taskAssigned'
  | 'taskCompleted'
  | 'workspaceInvitation'
  | 'workspaceMemberAdded'
  | 'workspaceMemberRemoved'
  | 'workspaceMemberRoleChanged'
  | 'spaceShared'
  | 'commentAdded'
  | 'mention'
  | 'agentFailure'
  | 'agentRecovered'
  | 'integrationFailure'
  | 'integrationRecovered'
  | 'performanceAlert'
  | 'systemAlert'
  | 'sidecarCompletion'
  | 'dailyReport'

// Default notification templates
export const NOTIFICATION_TEMPLATES: Record<NotificationType, NotificationTemplate> = {
  task_due_soon: {
    type: 'task_due_soon',
    title: 'Task Due Soon',
    message: '"{taskTitle}" is due in {timeRemaining}',
    defaultChannels: ['desktop', 'in_app'],
    defaultPriority: 'medium'
  },
  task_overdue: {
    type: 'task_overdue',
    title: 'Task Overdue',
    message: '"{taskTitle}" is overdue by {overdueTime}',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'high'
  },
  task_assigned: {
    type: 'task_assigned',
    title: 'Task Assigned',
    message: 'You have been assigned "{taskTitle}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  task_completed: {
    type: 'task_completed',
    title: 'Task Completed',
    message: '"{taskTitle}" has been completed',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  workspace_invitation: {
    type: 'workspace_invitation',
    title: 'Workspace Invitation',
    message: 'You have been invited to join "{workspaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  workspace_member_added: {
    type: 'workspace_member_added',
    title: 'New Workspace Member',
    message: '{memberName} has joined "{workspaceName}"',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  workspace_member_removed: {
    type: 'workspace_member_removed',
    title: 'Workspace Member Removed',
    message: '{memberName} has been removed from "{workspaceName}"',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  workspace_member_role_changed: {
    type: 'workspace_member_role_changed',
    title: 'Workspace Role Changed',
    message: '{memberName} role changed to {newRole} in "{workspaceName}"',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  workspace_badge_earned: {
    type: 'workspace_badge_earned',
    title: 'Badge Earned',
    message: 'You earned "{badgeName}" in "{workspaceName}"',
    defaultChannels: ['desktop', 'in_app'],
    defaultPriority: 'medium'
  },
  workspace_goal_reached: {
    type: 'workspace_goal_reached',
    title: 'Goal Reached',
    message: '"{goalName}" reached its target in "{workspaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  workspace_kudos_received: {
    type: 'workspace_kudos_received',
    title: 'Kudos Received',
    message: '{senderName} gave you kudos in "{workspaceName}"',
    defaultChannels: ['desktop', 'in_app'],
    defaultPriority: 'medium'
  },
  workspace_weekly_team_summary: {
    type: 'workspace_weekly_team_summary',
    title: 'Weekly Team Summary',
    message: 'Your team summary for "{workspaceName}" is ready',
    defaultChannels: ['email', 'in_app'],
    defaultPriority: 'low'
  },
  workspace_llm_access_request: {
    type: 'workspace_llm_access_request',
    title: 'LLM Access Request',
    message: '{memberName} requested access to "{workspaceName}" LLM calls',
    defaultChannels: ['in_app'],
    defaultPriority: 'high'
  },
  space_invitation: {
    type: 'space_invitation',
    title: 'Space Invitation',
    message: 'You have been invited to join "{spaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  space_invitation_cancelled: {
    type: 'space_invitation_cancelled',
    title: 'Space Invitation Cancelled',
    message: 'Your invitation to "{spaceName}" has been cancelled',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  space_invitation_declined: {
    type: 'space_invitation_declined',
    title: 'Space Invitation Declined',
    message: '{memberName} declined the invitation to "{spaceName}"',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  space_joined: {
    type: 'space_joined',
    title: 'Joined Space',
    message: 'You joined "{spaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'low'
  },
  space_member_added: {
    type: 'space_member_added',
    title: 'Space Member Added',
    message: '{memberName} was added to "{spaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'low'
  },
  space_member_removed: {
    type: 'space_member_removed',
    title: 'Space Member Removed',
    message: '{memberName} was removed from "{spaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'low'
  },
  space_member_role_changed: {
    type: 'space_member_role_changed',
    title: 'Space Role Changed',
    message: 'Your role changed to {newRole} in "{spaceName}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'low'
  },
  space_shared: {
    type: 'space_shared',
    title: 'Space Shared',
    message: '"{spaceName}" has been shared with you',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  comment_added: {
    type: 'comment_added',
    title: 'New Comment',
    message: '{authorName} commented on "{itemTitle}"',
    defaultChannels: ['desktop', 'in_app'],
    defaultPriority: 'medium'
  },
  mention: {
    type: 'mention',
    title: 'You Were Mentioned',
    message: '{authorName} mentioned you in "{itemTitle}"',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'high'
  },
  reminder: {
    type: 'reminder',
    title: 'Reminder',
    message: '{reminderMessage}',
    defaultChannels: ['desktop', 'in_app'],
    defaultPriority: 'medium'
  },
  agent_failure: {
    type: 'agent_failure',
    title: 'Agent Failure',
    message: '"{agentName}" needs attention',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'high'
  },
  agent_recovered: {
    type: 'agent_recovered',
    title: 'Agent Recovered',
    message: '"{agentName}" is operational again',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  integration_failure: {
    type: 'integration_failure',
    title: 'Integration Failure',
    message: '"{integrationName}" needs attention',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'high'
  },
  integration_recovered: {
    type: 'integration_recovered',
    title: 'Integration Recovered',
    message: '"{integrationName}" is operational again',
    defaultChannels: ['in_app'],
    defaultPriority: 'low'
  },
  performance_alert: {
    type: 'performance_alert',
    title: 'Performance Alert',
    message: 'Losi detected a performance issue',
    defaultChannels: ['email', 'in_app'],
    defaultPriority: 'medium'
  },
  system_alert: {
    type: 'system_alert',
    title: 'System Alert',
    message: 'Losi needs your attention',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'high'
  },
  sidecar_completion: {
    type: 'sidecar_completion',
    title: 'Sidecar Completion',
    message: 'Your Sidecar task is ready',
    defaultChannels: ['desktop', 'email', 'in_app'],
    defaultPriority: 'medium'
  },
  daily_report: {
    type: 'daily_report',
    title: 'Daily Report',
    message: 'Your Losi summary is ready',
    defaultChannels: ['email', 'in_app'],
    defaultPriority: 'low'
  }
}

// Default notification settings
export const DEFAULT_NOTIFICATION_SETTINGS: Omit<
  NotificationSettings,
  'id' | 'userId' | 'createdAt' | 'updatedAt'
> = {
  taskDueSoon: {
    enabled: true,
    channels: ['desktop', 'in_app'],
    advanceTime: 60, // 1 hour before
    priority: 'medium'
  },
  taskOverdue: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'high'
  },
  taskStarting: {
    enabled: true,
    channels: ['desktop', 'in_app'],
    priority: 'medium'
  },
  taskAssigned: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'medium'
  },
  taskCompleted: {
    enabled: false,
    channels: ['in_app'],
    priority: 'low'
  },
  workspaceInvitation: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'medium'
  },
  workspaceMemberAdded: {
    enabled: false,
    channels: ['in_app'],
    priority: 'low'
  },
  workspaceMemberRemoved: {
    enabled: false,
    channels: ['in_app'],
    priority: 'low'
  },
  workspaceMemberRoleChanged: {
    enabled: false,
    channels: ['in_app'],
    priority: 'low'
  },
  spaceShared: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'medium'
  },
  commentAdded: {
    enabled: true,
    channels: ['desktop', 'in_app'],
    priority: 'medium'
  },
  mention: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'high'
  },
  agentFailure: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'high'
  },
  agentRecovered: {
    enabled: true,
    channels: ['in_app'],
    priority: 'low'
  },
  integrationFailure: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'high'
  },
  integrationRecovered: {
    enabled: true,
    channels: ['in_app'],
    priority: 'low'
  },
  performanceAlert: {
    enabled: true,
    channels: ['email', 'in_app'],
    priority: 'medium'
  },
  systemAlert: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'high'
  },
  sidecarCompletion: {
    enabled: true,
    channels: ['desktop', 'email', 'in_app'],
    priority: 'medium'
  },
  dailyReport: {
    enabled: true,
    channels: ['email', 'in_app'],
    priority: 'low'
  },
  quietHours: {
    enabled: false,
    startTime: '22:00',
    endTime: '08:00',
    timezone: 'UTC'
  },
  emailDigest: {
    enabled: true,
    frequency: 'daily',
    time: '09:00'
  },
  desktopNotifications: {
    enabled: true,
    showPreview: true,
    sound: true
  }
}
